import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import { getFirebaseAuth } from './firebase-init';

// Auth providers
export const googleProvider = new GoogleAuthProvider();

// Auth functions with async initialization
export const signInWithEmail = async (email: string, password: string) => {
  const auth = await getFirebaseAuth();
  return signInWithEmailAndPassword(auth, email, password);
};

export const signUpWithEmail = async (email: string, password: string) => {
  const auth = await getFirebaseAuth();
  return createUserWithEmailAndPassword(auth, email, password);
};

export const logOut = async () => {
  const auth = await getFirebaseAuth();
  return signOut(auth);
};

// Auth state observer
export const onAuthChange = async (callback: (user: User | null) => void) => {
  const auth = await getFirebaseAuth();
  return onAuthStateChanged(auth, callback);
};

// Get current user
export const getCurrentUser = async () => {
  const auth = await getFirebaseAuth();
  return auth.currentUser;
};

// Get ID token
export const getIdToken = async (forceRefresh: boolean = false): Promise<string | null> => {
  const auth = await getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) return null;
  
  try {
    // Only force refresh when explicitly requested
    return await user.getIdToken(forceRefresh);
  } catch (error) {
    // Error logging removed for security
    return null;
  }
};

// Refresh token if needed
export const refreshToken = async (): Promise<string | null> => {
  const auth = await getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) return null;
  
  try {
    return await user.getIdToken(true); // Force refresh
  } catch (error) {
    // Error logging removed for security
    return null;
  }
};

// Export auth getter for components that need direct access
export { getFirebaseAuth as getAuth };

// Passwordless email-link (magic-link) sign-in.
export const MAGIC_LINK_EMAIL_KEY = 'digistore:magicLinkEmail';

const BEARER_PARAMS = ['oobCode', 'apiKey', 'mode', 'lang', 'continueUrl', 'tenantId'];

export const isMagicLink = async (href: string): Promise<boolean> => {
  const auth = await getFirebaseAuth();
  return isSignInWithEmailLink(auth, href);
};

export const sendMagicLink = async (email: string): Promise<void> => {
  const auth = await getFirebaseAuth();
  const normalized = email.trim().toLowerCase();
  await sendSignInLinkToEmail(auth, normalized, {
    url: `${window.location.origin}/login`, // C3: code-owned continue URL
    handleCodeInApp: true,
  });
  window.localStorage.setItem(MAGIC_LINK_EMAIL_KEY, normalized);
};

// Strip the magic-link bearer params from the address bar without a reload (C5).
export const cleanMagicLinkUrl = (): void => {
  const url = new URL(window.location.href);
  BEARER_PARAMS.forEach((p) => url.searchParams.delete(p));
  window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
};

// If the current URL is a magic link, complete sign-in and return the user.
// Email source: localStorage (set at send, or pre-seeded by E2E automation), else a prompt.
export const completeMagicLinkIfPresent = async (): Promise<User | null> => {
  const auth = await getFirebaseAuth();
  const href = window.location.href;
  if (!isSignInWithEmailLink(auth, href)) return null;
  try {
    let email = window.localStorage.getItem(MAGIC_LINK_EMAIL_KEY);
    if (!email) email = window.prompt('Confirm your email to finish signing in') || '';
    if (!email) return null;
    const credential = await signInWithEmailLink(auth, email, href);
    window.localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
    return credential.user;
  } finally {
    // Always strip bearer params (oobCode/apiKey/...) on every exit path —
    // success, sign-in failure, or no-email/cancelled-prompt — so the token
    // never lingers in the URL/history.
    cleanMagicLinkUrl();
  }
};